import { InfoTooltip } from '../InfoTooltip.js';
import { METRIC_EXPLANATIONS } from '../../data/metric-explanations.js';

export interface LockerIntelData {
  lockerName: string | null;
  isLiquidityLocked: boolean;
  lockDurationDays: number | null;
  unlockDate: string | null;
  percentLocked: number | null;
  lockerTrustLevel: string;
  lockerTrustScore: number;
  dataUnavailableReason?: string;
}

const FIELD_UNAVAILABLE = 'Not enough public data yet';

function trustColor(level: string): string {
  if (level === 'HIGH') return '#27ae60';
  if (level === 'MEDIUM') return '#f39c12';
  if (level === 'LOW') return '#e67e22';
  return '#888';
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

export function LockerIntelPanel({ data }: { data: LockerIntelData | null | undefined }) {
  const d = data ?? null;

  if (!d) return null;

  if (d.dataUnavailableReason && !d.isLiquidityLocked) {
    return (
      <div className="dash-card ws-forensic-section">
        <h3 className="ws-forensic-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          Liquidity Lock Analysis
          <InfoTooltip explain={METRIC_EXPLANATIONS.lockerIntel} compact />
        </h3>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Lock Status</span>
            <span className="intel-metric-value" style={{ fontSize: '0.85rem' }}>
              {d.dataUnavailableReason || FIELD_UNAVAILABLE}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dash-card ws-forensic-section">
      <h3 className="ws-forensic-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        Liquidity Lock Analysis
        <InfoTooltip explain={METRIC_EXPLANATIONS.lockerIntel} compact />
      </h3>
      <div className="intel-grid intel-grid-3">
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">LP Locked</span>
            <span className={`intel-bool-badge ${d.isLiquidityLocked ? 'intel-bool-good' : 'intel-bool-bad'}`}>
              {d.isLiquidityLocked ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Lock Platform</span>
            <span className="intel-metric-value" style={{ fontSize: '0.85rem' }}>
              {d.lockerName || FIELD_UNAVAILABLE}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">
              Platform Trust
              <InfoTooltip explain={METRIC_EXPLANATIONS.lockerIntel} compact />
            </span>
            <span className={`intel-bool-badge`} style={{ backgroundColor: trustColor(d.lockerTrustLevel), color: '#fff' }}>
              {d.lockerTrustLevel === 'HIGH' ? 'Trusted' : d.lockerTrustLevel === 'MEDIUM' ? 'Moderate' : d.lockerTrustLevel === 'LOW' ? 'Low Trust' : 'Unknown'}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Percent Locked</span>
            <span className="intel-metric-value">
              {d.percentLocked != null ? `${d.percentLocked.toFixed(1)}%` : FIELD_UNAVAILABLE}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Lock Duration</span>
            <span className="intel-metric-value">
              {d.lockDurationDays != null ? `${d.lockDurationDays.toLocaleString('en-US')} days` : FIELD_UNAVAILABLE}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Unlock Date</span>
            <span className="intel-metric-value" style={{ fontSize: '0.85rem' }}>
              {d.unlockDate ? formatDate(d.unlockDate) : FIELD_UNAVAILABLE}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
